/**
 * The only way this app talks to its API.
 *
 * Attaches the access token, parses the error envelope the Worker guarantees,
 * and turns a failure into a typed ApiError carrying the request id. Nothing
 * calls fetch("/api/...") directly, so there is one place to change when auth
 * or error handling changes.
 */

import type { SupabaseClient } from "@supabase/supabase-js";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId: string | null,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** True when the right response is to send the person back to the login page. */
  get isAuthFailure(): boolean {
    return this.code === "unauthenticated" || this.code === "invalid_token";
  }
}

interface ErrorEnvelope {
  error?: { code?: string; message?: string; requestId?: string };
}

export function createApiClient(supabase: SupabaseClient) {
  return async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;

    const headers = new Headers(init.headers);
    headers.set("accept", "application/json");
    if (token) headers.set("authorization", `Bearer ${token}`);
    if (init.body !== undefined && !headers.has("content-type")) {
      headers.set("content-type", "application/json");
    }

    const response = await fetch(path, { ...init, headers });
    const requestId = response.headers.get("x-request-id");

    // The Worker tells us when a token predates its membership and a refresh
    // will put later requests on the fast path.
    if (response.headers.get("x-cubby-refresh-session") === "1") {
      void supabase.auth.refreshSession();
    }

    if (response.status === 204) return undefined as T;

    const payload: unknown = await response.json().catch(() => null);

    if (!response.ok) {
      const envelope = (payload ?? {}) as ErrorEnvelope;
      throw new ApiError(
        response.status,
        envelope.error?.code ?? "internal",
        envelope.error?.message ?? "Something went wrong.",
        envelope.error?.requestId ?? requestId,
      );
    }

    return payload as T;
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;

/**
 * For the few endpoints that return a file instead of JSON (CSV exports).
 * Same token handling and error envelope as api(), but hands back the Blob.
 */
export function createDownloadClient(supabase: SupabaseClient) {
  return async function download(path: string): Promise<{ blob: Blob; filename: string | null }> {
    const { data } = await supabase.auth.getSession();
    const headers = new Headers();
    if (data.session?.access_token) headers.set("authorization", `Bearer ${data.session.access_token}`);
    const response = await fetch(path, { headers });
    if (!response.ok) {
      const envelope = ((await response.json().catch(() => null)) ?? {}) as ErrorEnvelope;
      throw new ApiError(
        response.status,
        envelope.error?.code ?? "internal",
        envelope.error?.message ?? "Could not download that file.",
        envelope.error?.requestId ?? response.headers.get("x-request-id"),
      );
    }
    const disposition = response.headers.get("content-disposition") ?? "";
    const match = disposition.match(/filename="([^"]+)"/);
    return { blob: await response.blob(), filename: match?.[1] ?? null };
  };
}

export type DownloadClient = ReturnType<typeof createDownloadClient>;

/** A message safe to show, with the request id when there is one. */
export function describeApiError(error: unknown, fallback = "Something went wrong."): string {
  if (error instanceof ApiError) {
    return error.requestId && error.status >= 500 ? `${error.message} (request ${error.requestId})` : error.message;
  }
  return fallback;
}
